import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronLeft, ChevronRight, Copy, Layers, Loader2, RotateCw } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useToast } from '../components/ui/Toast';
import { useCustomerDuplicatePairs, PAGE_SIZE } from '../hooks/useCustomerDuplicatePairs';
import { useCustomerIdentityGroups, GROUP_PAGE_SIZE } from '../hooks/useCustomerIdentityGroups';
import { customerDuplicatesApi } from '../services/customerDuplicatesApi';
import { KpiRow } from '../components/customer-duplicates/KpiRow';
import { FiltersBar } from '../components/customer-duplicates/FiltersBar';
import { RejectedCandidates } from '../components/customer-duplicates/RejectedCandidates';
import { PairsTable } from '../components/customer-duplicates/PairsTable';
import { GroupsKpiRow } from '../components/customer-duplicates/GroupsKpiRow';
import { GroupsFiltersBar } from '../components/customer-duplicates/GroupsFiltersBar';
import { GroupsTable } from '../components/customer-duplicates/GroupsTable';

/**
 * Customer Duplicates — the case file for the duplicate-customer master (masterplan
 * `2026-09-03-doppelkunden-kundenstamm.md`, phases P3 and P4).
 *
 * Two tabs, two grains:
 *   - **Pairs** (P3) is the work queue: two accounts at a time, with a reason and a decision.
 *   - **Groups** (P4) is the group-wide identity: one customer across all eight companies, with
 *     the total balance that exists in no other view, and the warning when one company carries
 *     the account blocked or badly overdue while another carries it open.
 *
 * Everything here reads pre-computed rows — the candidates from `CustomerDuplicatePair`
 * (P1/migration 178), the register results (P2/migration 179) and the groups from
 * `CustomerIdentityGroup` (P4/migration 181). This page runs no detection and no group build.
 *
 * DEEP LINKS. `/customer-duplicates?tab=groups&customer=<CustomerID>` opens the Groups tab with
 * that customer's group expanded — that is what the hints on `/customer-360` and
 * `/accounts-receivable` point at. The link carries a CUSTOMER id rather than a group id on
 * purpose: a group whose anchor member leaves is re-created under a new id by the next build, so
 * a group-id link would rot silently.
 */
export function CustomerDuplicates() {
    const { canView, canEdit, isLoading: permLoading } = usePagePermissions('/customer-duplicates');
    const { toast } = useToast();
    const [searchParams, setSearchParams] = useSearchParams();

    const urlTab = searchParams.get('tab') === 'groups' ? 'groups' : 'pairs';
    const [tab, setTab] = useState<'pairs' | 'groups'>(urlTab);
    useEffect(() => { setTab(urlTab); }, [urlTab]);

    const pairs = useCustomerDuplicatePairs(canView && tab === 'pairs');
    const groups = useCustomerIdentityGroups(canView && tab === 'groups');

    const [exporting, setExporting] = useState(false);

    // A `?customer=` deep link is resolved once, into the group search box, so the row the caller
    // meant is the one on screen — and the customer id stays visible in the filter rather than
    // being an invisible piece of state nobody can clear.
    const deepLinkCustomer = searchParams.get('customer');
    const [resolvedDeepLink, setResolvedDeepLink] = useState<string | null>(null);
    useEffect(() => {
        if (!canView || tab !== 'groups' || !deepLinkCustomer || resolvedDeepLink === deepLinkCustomer) return;
        setResolvedDeepLink(deepLinkCustomer);
        groups.setFilters({ search: deepLinkCustomer });
    }, [canView, tab, deepLinkCustomer, resolvedDeepLink, groups]);

    const switchTab = (next: 'pairs' | 'groups') => {
        setTab(next);
        const params = new URLSearchParams(searchParams);
        if (next === 'groups') params.set('tab', 'groups'); else params.delete('tab');
        setSearchParams(params, { replace: true });
    };

    const doExport = async () => {
        setExporting(true);
        try {
            await customerDuplicatesApi.export(pairs.filters);
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Export failed', 'error');
        } finally {
            setExporting(false);
        }
    };

    if (permLoading) {
        return (
            <div className="flex h-64 items-center justify-center">
                <Loader2 className="h-6 w-6 animate-spin text-blue-500" />
            </div>
        );
    }

    if (!canView) {
        return (
            <div className="flex h-64 items-center justify-center">
                <p className="text-gray-500">You do not have access to this page.</p>
            </div>
        );
    }

    return (
        <div className="p-6 space-y-4">
            <div className="flex items-baseline justify-between gap-4">
                <h1 className="text-2xl font-bold text-gray-900">Customer Duplicates</h1>
                <p className="text-xs italic text-gray-500">
                    {tab === 'pairs'
                        ? 'Customers carried on more than one account — grouped, ranked by reason, ready to decide.'
                        : 'One customer across all eight companies — total balance, and where the companies disagree.'}
                </p>
            </div>

            <div className="border-b border-gray-200">
                <nav className="flex gap-6">
                    <TabButton
                        active={tab === 'pairs'} onClick={() => switchTab('pairs')}
                        icon={Copy} label="Pairs" hint="Two accounts at a time — the work queue"
                    />
                    <TabButton
                        active={tab === 'groups'} onClick={() => switchTab('groups')}
                        icon={Layers} label="Group view" hint="One customer across the eight companies"
                    />
                </nav>
            </div>

            {tab === 'pairs' ? (
                <>
                    <KpiRow
                        summary={pairs.summary}
                        activeReason={pairs.filters.reason}
                        activeCompany={pairs.filters.company}
                        activeLevel={pairs.filters.level}
                        activePresence={pairs.filters.presence}
                        onReasonClick={reason => pairs.setFilters({ reason })}
                        onCompanyClick={company => pairs.setFilters({ company })}
                        onLevelClick={level => pairs.setFilters({ level })}
                        onPresenceClick={presence => pairs.setFilters({ presence })}
                    />

                    <RejectedCandidates run={pairs.summary.latestRun} />

                    <FiltersBar
                        filters={pairs.filters}
                        activeFilterCount={pairs.activeFilterCount}
                        onChange={pairs.setFilters}
                        onClear={pairs.clearFilters}
                        onExport={() => void doExport()}
                        exporting={exporting}
                    />

                    <div className="flex items-center gap-3 text-sm text-gray-500">
                        <ReloadButton loading={pairs.loading} onClick={() => void pairs.reload()} />
                        {pairs.summary.latestRun && (
                            <span>
                                Last detection run #{pairs.summary.latestRun.runId}:{' '}
                                {pairs.summary.latestRun.pairsFound.toLocaleString('de-CH')} pairs
                                over {pairs.summary.latestRun.customersScanned.toLocaleString('de-CH')} customers
                            </span>
                        )}
                    </div>

                    {pairs.error && (
                        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{pairs.error}</div>
                    )}

                    <PairsTable
                        rows={pairs.rows}
                        total={pairs.total}
                        loading={pairs.loading}
                        activeFilterCount={pairs.activeFilterCount}
                        sortBy={pairs.sortBy}
                        sortOrder={pairs.sortOrder}
                        onSort={pairs.setSort}
                        canEdit={canEdit}
                        onDecided={() => void pairs.reload()}
                    />

                    <Pager
                        page={pairs.page} totalPages={pairs.totalPages} total={pairs.total}
                        pageSize={PAGE_SIZE} loading={pairs.loading} setPage={pairs.setPage}
                    />
                </>
            ) : (
                <>
                    <GroupsKpiRow
                        summary={groups.summary}
                        activeCompany={groups.filters.company}
                        activeWarning={groups.filters.warning}
                        multiCompany={groups.filters.multiCompany}
                        onCompanyClick={company => groups.setFilters({ company })}
                        onWarningClick={warning => groups.setFilters({ warning })}
                        onMultiCompanyClick={() => groups.setFilters({ multiCompany: !groups.filters.multiCompany })}
                    />

                    <GroupsFiltersBar
                        filters={groups.filters}
                        activeFilterCount={groups.activeFilterCount}
                        onChange={groups.setFilters}
                        onClear={groups.clearFilters}
                        lastBuild={groups.summary.lastBuild}
                        overdueDays={groups.summary.config.overdueDays}
                    />

                    <div className="flex items-center gap-3 text-sm text-gray-500">
                        <ReloadButton loading={groups.loading} onClick={() => void groups.reload()} />
                        <span>
                            A company "sees a problem" at {groups.summary.config.overdueDays}+ days overdue and
                            at least {groups.summary.config.minOpenAmountEur} EUR
                            {groups.summary.config.requireActiveCounterpart
                                ? ', and the other company only counts when its account can still trade'
                                : ', and any unblocked account counts as the other side'}
                            . No mail is sent.
                        </span>
                    </div>

                    {groups.error && (
                        <div className="rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{groups.error}</div>
                    )}

                    <GroupsTable
                        rows={groups.rows}
                        total={groups.total}
                        loading={groups.loading}
                        activeFilterCount={groups.activeFilterCount}
                        sortBy={groups.sortBy}
                        sortOrder={groups.sortOrder}
                        onSort={groups.setSort}
                        overdueDays={groups.summary.config.overdueDays}
                        initialOpenGroupId={
                            // A deep link that selected exactly one group opens it straight away.
                            resolvedDeepLink !== null && groups.rows.length === 1 ? groups.rows[0].groupId : null
                        }
                    />

                    <Pager
                        page={groups.page} totalPages={groups.totalPages} total={groups.total}
                        pageSize={GROUP_PAGE_SIZE} loading={groups.loading} setPage={groups.setPage}
                    />
                </>
            )}
        </div>
    );
}

function TabButton({ active, onClick, icon: Icon, label, hint }: {
    active: boolean; onClick: () => void;
    icon: React.ComponentType<{ className?: string }>; label: string; hint: string;
}) {
    return (
        <button
            onClick={onClick}
            title={hint}
            className={`flex items-center gap-2 pb-3 border-b-2 text-sm font-medium transition-colors ${
                active
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
        >
            <Icon className="w-4 h-4" />
            {label}
        </button>
    );
}

function ReloadButton({ loading, onClick }: { loading: boolean; onClick: () => void }) {
    return (
        <button
            onClick={onClick}
            disabled={loading}
            className="inline-flex h-8 items-center gap-2 rounded border border-gray-300 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
        >
            <RotateCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Reload
        </button>
    );
}

function Pager({ page, totalPages, total, pageSize, loading, setPage }: {
    page: number; totalPages: number; total: number; pageSize: number;
    loading: boolean; setPage: (p: number) => void;
}) {
    if (total <= pageSize) return null;
    return (
        <div className="flex items-center justify-between text-sm text-gray-600">
            <span>Page {page} of {totalPages}</span>
            <div className="flex items-center gap-2">
                <button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page <= 1 || loading}
                    className="inline-flex h-8 items-center gap-1 rounded border border-gray-300 px-2 hover:bg-gray-50 disabled:opacity-40"
                >
                    <ChevronLeft className="h-4 w-4" /> Previous
                </button>
                <button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page >= totalPages || loading}
                    className="inline-flex h-8 items-center gap-1 rounded border border-gray-300 px-2 hover:bg-gray-50 disabled:opacity-40"
                >
                    Next <ChevronRight className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}

export default CustomerDuplicates;
